/************************************************************************************
 * [COMPONENT]: L-HEADER
 * Controls main header functionality including cart interactions
 *************************************************************************************/

class LTAHeader extends HTMLElement {
	constructor() {
		super()

		this.cartLinkEls = []
	}

	connectedCallback() {
		this.cacheElements()
		this.addEventListeners()
	}

	cacheElements() {
		this.cartLinkEls = Array.from(this.querySelectorAll('[data-cart-link]'))
	}

	addEventListeners() {
		this.initStickyMode()
	}

	initStickyMode() {
		window.addEventListener('scroll', () => {
			const stickyThreshold = this.parentElement?.offsetHeight || 0
			this.parentElement?.classList.toggle('is-sticky-mode', window.scrollY > stickyThreshold)
		})
	}
}

customElements.define('l-header', LTAHeader)

/************************************************************************************
 * [COMPONENT]: L-HEADER-INLINE-MENU
 *************************************************************************************/

if (!customElements.get('l-header-inline-menu')) {
	class LTAHeaderInline extends HTMLElement {
		constructor() {
			super()

			this.OPEN_ANIMATION_DURATION = 600
			this.CLOSE_ANIMATION_DURATION = 250
			this.CLOSE_DELAY = 50
			this._handleSummaryClick = this._handleSummaryClick.bind(this)
			this._handleDocumentClick = this._handleDocumentClick.bind(this)
			this._handleLinkEnter = this._handleLinkEnter.bind(this)
			this._handleScroll = this._handleScroll.bind(this)
			this._handleWheel = this._handleWheel.bind(this)
			this._handleKeydown = this._handleKeydown.bind(this)
			this._activeTrap = null // keep track of which element has trapFocus
			this._closeTimers = new Map()
			this._cleanupTimers = new Map()
			this._detailListeners = new Map()
			this._megaLinkListeners = new Map()
		}

		_bindDetailListeners() {
			this._unbindDetailListeners()
			this.detailsEls = Array.from(this.querySelectorAll('details'))

			this.detailsEls.forEach((detailsEl) => {
				const summary = detailsEl.querySelector('summary')
				const megaMenuEl = detailsEl.querySelector('[data-mega-menu]')
				const handlers = {}

				if (summary) {
					handlers.summaryClick = (event) => this._handleSummaryClick(detailsEl, event)
					handlers.mouseenter = (event) => this._handleSummaryClick(detailsEl, event)
					handlers.mouseleave = () => this._scheduleClose(detailsEl)

					summary.addEventListener('click', handlers.summaryClick)
					detailsEl.addEventListener('mouseenter', handlers.mouseenter)
					detailsEl.addEventListener('mouseleave', handlers.mouseleave)
				}

				if (megaMenuEl) {
					handlers.megaEnter = () => this._clearCloseTimer(detailsEl)
					handlers.megaLeave = () => this._scheduleClose(detailsEl)

					megaMenuEl.addEventListener('mouseenter', handlers.megaEnter)
					megaMenuEl.addEventListener('mouseleave', handlers.megaLeave)
				}

				this._detailListeners.set(detailsEl, { summary, megaMenuEl, handlers })
			})

			this.querySelectorAll('[data-mega-menu] a').forEach((linkEl) => {
				const handler = () => {
					const detailsEl = linkEl.closest('details')
					if (detailsEl) this._closeWithAnimation(detailsEl)
				}

				linkEl.addEventListener('click', handler)
				this._megaLinkListeners.set(linkEl, handler)
			})
		}

		_unbindDetailListeners() {
			this._detailListeners.forEach(({ summary, megaMenuEl, handlers }, detailsEl) => {
				if (summary && handlers.summaryClick) {
					summary.removeEventListener('click', handlers.summaryClick)
				}

				if (handlers.mouseenter) {
					detailsEl.removeEventListener('mouseenter', handlers.mouseenter)
				}

				if (handlers.mouseleave) {
					detailsEl.removeEventListener('mouseleave', handlers.mouseleave)
				}

				if (megaMenuEl && handlers.megaEnter) {
					megaMenuEl.removeEventListener('mouseenter', handlers.megaEnter)
				}

				if (megaMenuEl && handlers.megaLeave) {
					megaMenuEl.removeEventListener('mouseleave', handlers.megaLeave)
				}

				this._clearCloseTimer(detailsEl)
				this._clearCleanupTimer(detailsEl)
			})
			this._detailListeners.clear()

			this._megaLinkListeners.forEach((handler, linkEl) => {
				linkEl.removeEventListener('click', handler)
			})
			this._megaLinkListeners.clear()
		}

		connectedCallback() {
			this._bindDetailListeners()

			this.querySelectorAll('.l-hMenu__link').forEach((linkEl) => {
				linkEl.addEventListener('mouseenter', this._handleLinkEnter)
			})

			document.addEventListener('click', this._handleDocumentClick)
			document.addEventListener('keydown', this._handleKeydown)
			window.addEventListener('scroll', this._handleScroll, { passive: true })
			window.addEventListener('wheel', this._handleWheel, { passive: true })
		}

		disconnectedCallback() {
			this._unbindDetailListeners()

			this.querySelectorAll('.l-hMenu__link').forEach((linkEl) => {
				linkEl.removeEventListener('mouseenter', this._handleLinkEnter)
			})

			document.removeEventListener('click', this._handleDocumentClick)
			document.removeEventListener('keydown', this._handleKeydown)
			window.removeEventListener('scroll', this._handleScroll)
			window.removeEventListener('wheel', this._handleWheel)
		}

		_getOpenDetails() {
			return this.detailsEls.filter((el) => el.open && !el.classList.contains('is-closing'))
		}

		_closeAllOpenMenus() {
			const openEls = this._getOpenDetails()
			openEls.forEach((el) => this._closeWithAnimation(el))

			if (openEls.length > 0) {
				l_removeTrapFocus()
				this._activeTrap = null
			}
		}

		_handleScroll() {
			if (!this._hasOpenMegaMenu()) return
			this._closeAllOpenMenus()
		}

		_handleWheel() {
			if (!this._hasOpenMegaMenu()) return
			this._closeAllOpenMenus()
		}

		_handleKeydown(event) {
			if (event.code !== 'Escape') return
			if (!this._getOpenDetails().length) return
			this._closeAllOpenMenus()
		}

		_scheduleClose(detailsEl) {
			this._clearCloseTimer(detailsEl)
			const timer = setTimeout(() => this._closeWithAnimation(detailsEl), this.CLOSE_DELAY)
			this._closeTimers.set(detailsEl, timer)
		}

		_clearCloseTimer(detailsEl) {
			const timer = this._closeTimers.get(detailsEl)
			if (timer) {
				clearTimeout(timer)
				this._closeTimers.delete(detailsEl)
			}
		}

		_clearCleanupTimer(detailsEl) {
			const timer = this._cleanupTimers.get(detailsEl)
			if (timer) {
				clearTimeout(timer)
				this._cleanupTimers.delete(detailsEl)
			}
		}

		_hasOpenMegaMenu() {
			return this.detailsEls.some(
				(el) => el.open && !el.classList.contains('is-closing') && el.querySelector('[data-mega-menu]')
			)
		}

		async _handleSummaryClick(toggledEl, event) {
			event.preventDefault()

			this.detailsEls.forEach((el) => this._clearCloseTimer(el))

			const isMegaMenu = !!toggledEl.querySelector('[data-mega-menu]')
			const isClick = event.type === 'click'
			const isAlreadyOpen = toggledEl.open && !toggledEl.classList.contains('is-closing')

			if (isClick && isMegaMenu && isAlreadyOpen) {
				this._closeWithAnimation(toggledEl)
				return
			}

			const level = toggledEl.dataset.level
			const openEls = this.detailsEls.filter(
				(el) => el.dataset.level === level && el !== toggledEl && el.open && !el.classList.contains('is-closing')
			)

			this._openWithAnimation(toggledEl)

			// Close all other open siblings
			openEls.forEach((el) => this._closeWithAnimation(el))
		}

		_handleLinkEnter() {
			this.detailsEls.forEach((el) => this._clearCloseTimer(el))

			const openEls = this.detailsEls.filter(
				(el) => el.dataset.level === '1' && el.open && !el.classList.contains('is-closing')
			)

			openEls.forEach((el) => {
				this._clearCloseTimer(el)
				this._closeWithAnimation(el)
			})
		}

		_openWithAnimation(detailsEl) {
			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return

			this._clearCloseTimer(detailsEl)
			this._clearCleanupTimer(detailsEl)
			detailsEl.classList.remove('is-closing')

			const isMegaMenu = !!detailsEl.querySelector('[data-mega-menu]')
			const level = detailsEl.dataset.level
			const openingMenuEl = this.detailsEls.find(
				(el) => el !== detailsEl && el.dataset.level === level && el.open && !el.classList.contains('is-closing')
			)

			detailsEl.classList.add('is-opening')
			detailsEl.setAttribute('open', '')

			if (isMegaMenu) {
				content.style.height = ''
				content.style.overflow = ''
				content.style.opacity = ''
				content.style.transition = ''
			} else {
				if (!openingMenuEl) {
					content.style.height = '0px'
				}

				content.style.overflow = 'hidden'
				content.style.transition = `height ${this.OPEN_ANIMATION_DURATION}ms ease`

				requestAnimationFrame(() => {
					content.style.height = `${content.scrollHeight}px`

					if (openingMenuEl) {
						content.style.opacity = '0'
						content.style.transition = `opacity ${this.OPEN_ANIMATION_DURATION}ms ease`
						content.style.overflow = ''

						requestAnimationFrame(() => {
							content.style.opacity = '1'
						})
					}
				})
			}

			if (isMegaMenu) {
				l_lockBodyScroll('header-inline-menu')
			}
		}

		_closeWithAnimation(detailsEl) {
			if (detailsEl.classList.contains('is-closing')) return
			if (!detailsEl.open && !detailsEl.classList.contains('is-opening')) return

			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return

			// Close nested details recursively
			const nestedDetails = Array.from(detailsEl.querySelectorAll(':scope details[open]'))
			nestedDetails.forEach((child) => this._closeWithAnimation(child))

			const isMegaMenu = !!detailsEl.querySelector('[data-mega-menu]')

			detailsEl.classList.remove('is-opening')
			detailsEl.classList.add('is-closing')

			if (!isMegaMenu) {
				const startHeight = content.scrollHeight

				content.style.height = `${startHeight}px`
				content.style.overflow = 'hidden'
				content.style.transition = `height ${this.CLOSE_ANIMATION_DURATION}ms ease`

				requestAnimationFrame(() => {
					content.style.height = '0px'
				})
			} else {
				const isSwitching = this._hasOpenMegaMenu()

				if (!isSwitching) {
					content.style.opacity = '1'
					content.style.transition = `opacity ${this.CLOSE_ANIMATION_DURATION}ms ease`

					requestAnimationFrame(() => {
						content.style.opacity = '0'
					})

					l_unLockBodyScroll('header-inline-menu')
				} else {
					content.style.opacity = '0'
					content.style.transition = ''
				}
			}

			this._clearCleanupTimer(detailsEl)
			const timer = setTimeout(() => {
				detailsEl.removeAttribute('open')
				detailsEl.classList.remove('is-closing')

				content.style.height = ''
				content.style.overflow = ''
				content.style.transition = ''
				content.style.opacity = ''

				this._cleanupTimers.delete(detailsEl)
			}, this.CLOSE_ANIMATION_DURATION)
			this._cleanupTimers.set(detailsEl, timer)
		}

		_handleDocumentClick(event) {
			if (this.contains(event.target)) return // ignore inside clicks
			this._closeAllOpenMenus()
		}
	}

	customElements.define('l-header-inline-menu', LTAHeaderInline)
}

/************************************************************************************
 * [MODULE]: MOBILE MENU DRAWERS
 * Controls mobile menu drawers functionality
 *************************************************************************************/

class LTAMainMenuDrawer extends LTADrawer {
	drawer_close() {
		super.drawer_close()
	}
}

customElements.define('l-main-menu-drawer', LTAMainMenuDrawer)

class LTAMainMenuDrawerLv3 extends LTADrawer {
	setupCloseEvent() {
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', (event) => {
				if (closeBtnEl.hasAttribute('data-close')) {
					event.stopPropagation()
				}

				this.drawer_close()
			})
		})
	}
}

customElements.define('l-drawer-drawer-lv3', LTAMainMenuDrawerLv3)

/************************************************************************************
 * [MODULE]: SEARCH / PREDICTIVE SEARCH MODULE
 *************************************************************************************/

class LTASearchDrawer extends LTADrawer {
	connectedCallback() {
		super.connectedCallback()
		this._cacheElements()
		this._initCache()
		this._addEventListeners()
	}

	_cacheElements() {
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchButtonEl = this.querySelector('[data-search-button]')
		this.searchResultsEl = this.querySelector('[data-results]')
		this.searchDefaultEl = this.querySelector('[data-search-default]')
	}

	_initCache() {
		this.cachedResults = {}
	}

	drawer_close() {
		super.drawer_close()
		// Reset so the next open starts on the default (pre-typing) state.
		if (this.searchInputEl) this.searchInputEl.value = ''
		this._toggleSearchResults(false)
		this._toggleDefault(true)
	}

	_addEventListeners() {
		const debouncedInput = l_debounce(this.handleSearchInput.bind(this), 300)
		this.searchInputEl.addEventListener('input', debouncedInput)
	}

	handleSearchInput() {
		const searchTerm = this._normalizeSearchTerm(this.searchInputEl.value)

		if (!searchTerm) {
			this._toggleSearchResults(false)
			this._toggleDefault(true)
			return
		}

		this._toggleDefault(false)
		this._toggleSearchResults(true)
		this._setLoading(true)

		if (this.cachedResults[searchTerm]) {
			this._renderSearchResults(this.cachedResults[searchTerm])
			return
		}

		this._fetchSearchResults(searchTerm)
	}

	_normalizeSearchTerm(term) {
		if (!term) return ''
		return term.trim().replace(/ /g, '-').toLowerCase()
	}

	_fetchSearchResults(searchTerm) {
		const baseURL = window.LTATheme.routes.predictive_search_url
		const params = `?q=${encodeURIComponent(searchTerm)}&section_id=a-ajax-header-predictive-search`
		const searchURL = `${baseURL}${params}`

		fetch(searchURL)
			.then((response) => response.text())
			.then((htmlText) => {
				const resultsMarkup = l_getSectionDOM(htmlText, '#shopify-section-a-ajax-header-predictive-search')
				this.cachedResults[searchTerm] = resultsMarkup
				l_debounce(() => this._renderSearchResults(resultsMarkup), 100)()
			})
			.catch(console.error)
			.finally(() => this._setLoading(false))
	}

	_renderSearchResults(resultsMarkup) {
		this.searchResultsEl.innerHTML = resultsMarkup.innerHTML
		l_trapFocus(this, this.searchInputEl, false)
		this._setLoading(false)
	}

	_setLoading(isLoading) {
		this.searchButtonEl.classList.toggle('l-button-loading', isLoading)
	}

	_toggleSearchResults(show) {
		if (show) {
			this.searchResultsEl.classList.remove('l-hidden')
			this.searchResultsEl.removeAttribute('hidden')
			this.searchResultsEl.removeAttribute('tabindex')
		} else {
			this.searchResultsEl.classList.add('l-hidden')
			this.searchResultsEl.setAttribute('hidden', '')
			this.searchResultsEl.setAttribute('tabindex', '-1')
		}
	}

	_toggleDefault(show) {
		if (!this.searchDefaultEl) return

		if (show) {
			this.searchDefaultEl.classList.remove('l-hidden')
			this.searchDefaultEl.removeAttribute('hidden')
			this.searchDefaultEl.removeAttribute('tabindex')
		} else {
			this.searchDefaultEl.classList.add('l-hidden')
			this.searchDefaultEl.setAttribute('hidden', '')
			this.searchDefaultEl.setAttribute('tabindex', '-1')
		}
	}
}

customElements.define('l-search-drawer', LTASearchDrawer)

